"use client";

// Le curseur de la démonstration. Chaque scène décrit son parcours en
// étapes `{ at, target, click }` : le curseur ARRIVE sur la cible à `at`
// (il part `move` millisecondes plus tôt) et clique à l'arrivée si
// `click` est vrai. `grab` appuie sans relâcher (début d'un glisser),
// `release` relâche à l'arrivée. La scène déclenche ses changements
// d'état sur les mêmes instants, c'est ce qui donne l'impression que le
// clic fait quelque chose.
//
// Les cibles sont des éléments de la scène, repérés par un nom
// (useTarget) et mesurés au moment où le curseur part vers eux : un
// libellé plus long en anglais déplace le bouton, le curseur suit.
//
// Sur mobile, pas de flèche : un doigt ne se voit pas avant de toucher.
// Le curseur devient une pastille qui n'apparaît qu'autour du toucher.

import { createContext, useCallback, useContext, useRef } from "react";
import {
    animate,
    cubicBezier,
    motion,
    useMotionValue,
    useMotionValueEvent,
} from "motion/react";

export const CURSOR_MOVE_MS = 720;

// Départ lent, arrivée douce : la main qui va chercher un bouton. Exporté
// pour qu'un élément tiré par le curseur (le curseur d'un réglage) suive
// exactement la même courbe que lui.
export const glide = cubicBezier(0.45, 0.05, 0.2, 1);

const TargetsContext = createContext(null);

export const TargetsProvider = TargetsContext.Provider;

/**
 * Déclare un élément de la scène comme cible possible du curseur.
 *
 * Rejouer le chapitre affiché fait coexister deux scènes identiques le temps
 * du fondu : celle qui sort ne doit effacer la cible que si c'est encore la
 * sienne, sinon le curseur de la nouvelle ne trouverait plus son bouton.
 */
export function useTarget(name) {
    const targets = useContext(TargetsContext);
    const elementRef = useRef(null);
    return useCallback(
        (element) => {
            if (!targets) return;
            if (element) {
                elementRef.current = element;
                targets.current.set(name, element);
            } else {
                if (targets.current.get(name) === elementRef.current) targets.current.delete(name);
                elementRef.current = null;
            }
        },
        [targets, name],
    );
}

export default function DemoCursor({
    time,
    track,
    stageRef,
    stageWidth,
    rest,
    touch = false,
    hidden = false,
}) {
    const targets = useContext(TargetsContext);
    const x = useMotionValue(rest.x);
    const y = useMotionValue(rest.y);
    const press = useMotionValue(1);
    const touchOpacity = useMotionValue(0);
    const rippleScale = useMotionValue(0.4);
    const rippleOpacity = useMotionValue(0);
    const runtime = useRef({ track: null, width: null, from: [], to: [], clicked: new Set() });

    // Mesure une cible en coordonnées de scène. `ax`/`ay` visent un point
    // de l'élément (0,5 = son centre), utile pour une position le long
    // d'une glissière.

    const measure = (step) => {
        if (step.point) return step.point;
        const element = targets?.current.get(step.target);
        const stage = stageRef.current;
        if (!element || !stage) return null;
        const stageRect = stage.getBoundingClientRect();
        if (!stageRect.width) return null;
        const rect = element.getBoundingClientRect();
        const ratio = stageRect.width / stageWidth;
        return {
            x: (rect.left - stageRect.left + rect.width * (step.ax ?? 0.5)) / ratio,
            y: (rect.top - stageRect.top + rect.height * (step.ay ?? 0.5)) / ratio,
        };
    };

    const click = () => {
        animate(press, [1, 0.84, 1], { duration: 0.3, ease: "easeOut" });
        rippleScale.set(0.4);
        rippleOpacity.set(0.55);
        animate(rippleScale, 1, { duration: 0.45, ease: [0.22, 1, 0.36, 1] });
        animate(rippleOpacity, 0, { duration: 0.45, ease: "easeOut" });
    };

    const hold = (down) => {
        animate(press, down ? 0.86 : 1, { duration: 0.16, ease: "easeOut" });
    };

    const update = (t) => {
        const state = runtime.current;
        // Nouveau chapitre, ou passage de la mise en page mobile à celle
        // d'ordinateur : les positions mesurées ne valent plus.
        if (state.track !== track || state.width !== stageWidth) {
            state.track = track;
            state.width = stageWidth;
            state.from = [];
            state.to = [];
            state.clicked = new Set();
            press.set(1);
        }
        if (!track?.length) return;

        // Étape en cours : la dernière dont le déplacement a commencé.
        let index = -1;
        for (let i = 0; i < track.length; i += 1) {
            if (t >= track[i].at - (track[i].move ?? CURSOR_MOVE_MS)) index = i;
        }

        if (touch) {
            // La pastille n'existe qu'autour d'un toucher, et pendant
            // qu'un doigt glisse.
            let opacity = 0;
            let grabbedAt = null;
            for (const step of track) {
                if (step.grab) grabbedAt = step.at;
                if (step.release && grabbedAt !== null) {
                    if (t >= grabbedAt && t <= step.at) opacity = 1;
                    const after = t - step.at;
                    if (after >= 0 && after < 380) opacity = Math.max(opacity, 1 - after / 380);
                    grabbedAt = null;
                }
                if (!step.click && !step.grab) continue;
                const before = step.at - t;
                const after = t - step.at;
                if (before >= 0 && before < 260) opacity = Math.max(opacity, 1 - before / 260);
                if (step.click && after >= 0 && after < 380) opacity = Math.max(opacity, 1 - after / 380);
            }
            touchOpacity.set(opacity);
        }

        if (index < 0) return;
        const step = track[index];
        const move = step.move ?? CURSOR_MOVE_MS;
        const start = step.at - move;

        const progress = Math.max(0, Math.min(1, (t - start) / move));
        if (!state.from[index]) state.from[index] = { x: x.get(), y: y.get() };
        // La cible se mesure pendant tout le trajet : elle peut finir son
        // apparition pendant que le curseur part vers elle. Une fois
        // arrivé, le curseur ne bouge plus.
        if (progress < 1 || !state.to[index]) state.to[index] = measure(step) ?? state.to[index];
        const from = state.from[index];
        const to = state.to[index];
        if (!to) return;

        const eased = glide(progress);
        // Une main ne va jamais tout droit : un léger arc, perpendiculaire
        // au trajet, qui s'annule au départ et à l'arrivée.
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const arc = Math.sin(Math.PI * progress) * Math.min(28, Math.hypot(dx, dy) * 0.06);
        const length = Math.hypot(dx, dy) || 1;
        x.set(from.x + dx * eased + (-dy / length) * arc);
        y.set(from.y + dy * eased + (dx / length) * arc);

        // Les arrivées se traitent toutes, pas seulement celle de l'étape en
        // cours : un glisser commence à l'instant même où la prise arrive,
        // et l'étape suivante est déjà en cours quand la prise tombe.
        track.forEach((reached, position) => {
            if (t < reached.at || state.clicked.has(position)) return;
            state.clicked.add(position);
            if (reached.grab) hold(true);
            if (reached.release) hold(false);
            // Un saut dans le temps (chapitre choisi à la main) ne rejoue
            // pas les clics déjà passés.
            if (reached.click && t - reached.at < 250) click();
        });
    };

    useMotionValueEvent(time, "change", update);

    if (touch) {
        return (
            <motion.div
                aria-hidden="true"
                className="pointer-events-none absolute left-0 top-0 z-50"
                style={{ x, y, opacity: hidden ? 0 : touchOpacity }}
            >
                <motion.span
                    className="absolute -left-[18px] -top-[18px] block h-9 w-9 rounded-full border border-white/70 bg-gray-900/20 shadow-[0_2px_10px_rgba(0,0,0,0.15)] backdrop-blur-[1px] dark:bg-white/25"
                    style={{ scale: press }}
                />
            </motion.div>
        );
    }

    return (
        <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-0 z-50 transition-opacity duration-200"
            style={{ x, y, opacity: hidden ? 0 : 1 }}
        >
            <motion.span
                className="absolute -left-4 -top-4 block h-8 w-8 rounded-full border-2 border-primary/60"
                style={{ scale: rippleScale, opacity: rippleOpacity }}
            />
            <motion.svg
                width="20"
                height="24"
                viewBox="0 0 20 24"
                className="absolute -left-[3px] -top-[2px] origin-top-left drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
                style={{ scale: press }}
            >
                <path
                    d="M3 2.2v17.6c0 .6.7.9 1.1.5l4.1-4 2.6 6.1c.2.4.6.6 1 .4l2.3-1c.4-.2.6-.6.4-1l-2.6-6h5.8c.6 0 .9-.7.5-1.1L4.1 1.7C3.7 1.3 3 1.6 3 2.2Z"
                    fill="#111827"
                    stroke="#ffffff"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                />
            </motion.svg>
        </motion.div>
    );
}
